import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AccessiblePrice } from '@/components/AccessiblePrice'
import type { ProductOfferFragment } from '@/services/graphql/generated'
import { StoryblokComponent } from '@storyblok/react'
import { Card } from 'ui/src/components/Card/Card'
import { Dialog } from 'ui/src/components/Dialog/Dialog'
import { LargeDialog } from 'ui/src/components/Dialog/LargeDialog'
import { Text } from 'ui/src/components/Text/Text'
import { yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { usePriceCalculatorDeductibleInfo } from '../priceCalculatorAtoms'
import { CardRadioGroup } from './CardRadioGroup/CardRadioGroup'
import { InfoButton } from './InfoButton/InfoButton'
import { textTransition, vars } from './CardRadioGroup/CardRadioGroup.css'

export type ProductOfferWithDeductible = ProductOfferFragment & {
  deductible: NonNullable<ProductOfferFragment['deductible']>
}

type Props = {
  offers: Array<ProductOfferWithDeductible>
  selectedOfferId: string
  onValueChange: (offerId: string) => void
}

export function DeductibleSelector({ offers, selectedOfferId, onValueChange }: Props) {
  const deductibleLevels = useMemo(
    () =>
      offers.map((offer) => {
        return {
          id: offer.id,
          price: offer.cost.baseInsuranceGross,
          title: offer.deductible.displayName,
          description: offer.deductible.tagline,
        }
      }),
    [offers],
  )

  return (
    <div className={yStack({ alignItems: 'stretch', gap: 'md' })}>
      <CardRadioGroup.Root value={selectedOfferId} onValueChange={onValueChange}>
        {deductibleLevels.map((item, index) => {
          const isSelected = selectedOfferId === item.id
          return (
            <CardRadioGroup.Item
              // NOTE: We want to avoid remounting selected deductible element when changing tiers
              // to avoid extra animation, hence index key
              key={index}
              className={yStack({ gap: 'lg' })}
              value={item.id}
              aria-labelledby={`${item.id}-deductible-title`}
              aria-describedby={
                item.description
                  ? `${item.id}-deductible-price ${item.id}-deductible-content`
                  : `${item.id}-deductible-price`
              }
            >
              <Card.Aside floating>
                <CardRadioGroup.Badge isSelected={isSelected}>
                  <span id={`${item.id}-deductible-price`}>
                    <AccessiblePrice price={item.price} />
                  </span>
                </CardRadioGroup.Badge>
              </Card.Aside>
              <Card.Header>
                <Card.Heading>
                  <Card.Title
                    id={`${item.id}-deductible-title`}
                    as="h2"
                    className={textTransition}
                    variant="standard.24"
                    style={{ color: vars.titleColor }}
                  >
                    {item.title}
                  </Card.Title>
                </Card.Heading>
              </Card.Header>

              {item.description && (
                <Text
                  id={`${item.id}-deductible-content`}
                  className={textTransition}
                  size="md"
                  color={isSelected ? 'textTertiary' : 'textSecondary'}
                >
                  {item.description}
                </Text>
              )}
            </CardRadioGroup.Item>
          )
        })}
      </CardRadioGroup.Root>

      <DeductibleInfo />
    </div>
  )
}

function DeductibleInfo() {
  const { t } = useTranslation(['common', 'purchase-form'])
  const deductibleInfo = usePriceCalculatorDeductibleInfo()

  return (
    <LargeDialog.Root>
      <Dialog.Trigger asChild={true}>
        <InfoButton>
          <div className={yStack()}>
            <Text as="div" size="xs">
              {t('purchase-form:DEDUCTIBLE_SELECTOR_DIALOG_TRIGGER')}
            </Text>
            <Text as="span" color="textSecondary" size="xs" balance={true}>
              {t('purchase-form:DEDUCTIBLE_SELECTOR_DIALOG_DESCRIPTION')}
            </Text>
          </div>
        </InfoButton>
      </Dialog.Trigger>
      <LargeDialog.Content className={sprinkles({ paddingBlock: 0 })}>
        <LargeDialog.CloseIconButton aria-label={t('common:DIALOG_CLOSE')} />
        <Dialog.Title className={sprinkles({ display: 'none' })}>
          {t('purchase-form:DEDUCTIBLE_SELECTOR_FOOTER_LINK')}
        </Dialog.Title>
        {deductibleInfo.map((blok) => (
          <StoryblokComponent key={blok._uid} blok={blok} nested={true} />
        ))}
      </LargeDialog.Content>
    </LargeDialog.Root>
  )
}
